// The platform layer: the host WebApp bridge, which messenger we're in,
// and the per-user gate/onboarding keys.
//
// webApp() is the ONLY way to reach the host bridge — never poke
// window.Telegram/Bale/Eitaa directly at a call site.
import Onboarding from "../Onboarding.jsx";
import { moduleLang } from "../i18n.jsx";
import { Stars } from "./emoji.jsx";
import { androidWebApp } from "./android.js";

// ── Platform layer ───────────────────────────────────────────────────────────
// The ONE place that talks to the host messenger. Every messenger we support
// injects a Telegram-compatible WebApp bridge, but under its own global — so
// resolve it here instead of repeating `window.Telegram?.WebApp || …` per call
// site (missing one is how a platform silently half-works).
// ⚠️ Which SDK OBJECT exists proves NOTHING about the host.
// index.html self-hosts telegram-web-app.js, eitaa-web-app.js AND
// soroush-web-app.js, and each assigns its global unconditionally
// (eitaa-web-app.js line ~2350: `window.Eitaa.WebApp = WebApp`; the Soroush SDK
// is a fork that does the same for window.Soroush). So window.Telegram.WebApp,
// window.Eitaa.WebApp and window.Soroush.WebApp are ALL present, on every platform.
//
// The reliable signal is what the HOST injects natively:
//   • window.EitaaWebviewProxy    — only Eitaa's WebView
//   • window.SoroushWebviewProxy  — only Soroush Plus's WebView
//   • window.BaleWebApp           — only Bale's WebView (a BARE global, not window.Bale)
//   • window.TelegramWebviewProxy — Telegram (and Telegram-compatible hosts)
// NOT window.SoroushGameProxy — the Soroush SDK defines that one itself (a Windows
// Phone back-compat shim), so it exists everywhere and would report "soroush" on
// every platform. Only the host-injected names above are trustworthy.
//
// ⚠️ `window.Bale?.WebApp` USED to be the Bale signal, on the reasoning that no
// script of ours defined it. That stopped being true on 2026-09-13, when we started
// self-hosting Bale's own SDK (index.html) — it assigns window.Bale unconditionally,
// exactly like the other three. Bale's native proxy is the bare global `BaleWebApp`
// (the SDK calls `BaleWebApp.postEvent(...)`), so that is the signal now. Had this
// check stayed, loading the SDK would have made TELEGRAM report "bale".
//
// Getting this wrong is invisible: postEvent just goes nowhere and every bridge
// call silently does nothing (that is why the Eitaa join button "did nothing" —
// we were driving the Telegram SDK, whose transport Eitaa doesn't listen to).
//   • window.ZohaAndroid          — only OUR Bazaar Android app (android/), see android.js
export function hostKey() {
  try {
    if (typeof window.ZohaAndroid !== "undefined") return "bazaar";
    if (typeof window.EitaaWebviewProxy !== "undefined") return "eitaa";
    if (typeof window.SoroushWebviewProxy !== "undefined") return "soroush";
    if (typeof window.BaleWebApp !== "undefined") return "bale";
    if (typeof window.TelegramWebviewProxy !== "undefined") return "telegram";
  } catch (_) {}
  return null;   // unknown (e.g. desktop/iframe) — caller picks a default
}

// The host bridge, matched to the host that actually injected a transport.
export function webApp() {
  const k = hostKey();
  if (k === "bazaar") return androidWebApp();
  if (k === "eitaa" && window.Eitaa?.WebApp) return window.Eitaa.WebApp;
  if (k === "soroush" && window.Soroush?.WebApp) return window.Soroush.WebApp;
  if (k === "bale" && window.Bale?.WebApp) return window.Bale.WebApp;
  return window.Telegram?.WebApp
      || window.Bale?.WebApp
      || window.Eitaa?.WebApp
      || window.Soroush?.WebApp
      || null;
}

// Open an external / deep link from inside a messenger WebView.
//
// ⚠️ `<a href target="_blank">` SILENTLY DOES NOTHING in these WebViews — the same
// trap as the story download. The link must be handed to the host bridge.
//
// Prefer the host's OWN deep-link opener so a channel opens INSIDE the messenger
// rather than bouncing out to a browser: Eitaa has openEitaaLink (eitaa.com only),
// Telegram has openTelegramLink (t.me only). Both VALIDATE the hostname and THROW
// on a mismatch, so every candidate is tried in order and we fall through to
// openLink, then window.open, then a plain navigation.
export function openExternal(url) {
  if (!url) return;
  const wa = webApp();
  let host = "";
  try { host = new URL(url).hostname; } catch (_) {}

  // NOTE Soroush deliberately has no entry here. Its SDK is a Telegram fork and
  // its openSoroushLink still hard-validates `hostname === "t.me"` (they never
  // changed it), so a splus.ir link THROWS there — it has to go through openLink.
  const attempts = [];
  if (wa) {
    if (host === "eitaa.com" && wa.openEitaaLink) attempts.push(() => wa.openEitaaLink(url));
    if (host === "t.me" && wa.openTelegramLink)   attempts.push(() => wa.openTelegramLink(url));
    if (wa.openLink)                              attempts.push(() => wa.openLink(url));
  }
  // window.open returns null when the WebView blocks it — treat that as a failure
  // so we still fall through to a direct navigation.
  attempts.push(() => { if (!window.open(url, "_blank")) throw new Error("popup blocked"); });
  attempts.push(() => { window.location.href = url; });

  for (const attempt of attempts) {
    try { attempt(); return; } catch (_) { /* try the next one */ }
  }
}

// Which platform the user came in on (set at login). Drives Toman-vs-Stars UI.
// Which messenger we're running inside, decided WITHOUT the server — the
// channel-join screen renders before /auth/login returns, so the first paint
// needs an answer. Uses the host-injected transport (see hostKey), NOT the SDK
// globals: both SDKs load on every platform, so checking window.Eitaa?.WebApp
// here would report "eitaa" everywhere.
// The server's platform_info still overrides this once login completes.
export function detectHostPlatform() {
  return hostKey() || "bale";
}

export function getPlatform() {
  try { return sessionStorage.getItem("platform") || detectHostPlatform(); } catch { return detectHostPlatform(); }
}

// Everything platform-specific the BACKEND told us at login (name, channel link,
// whether membership can be verified, whether payment exists). Cached in
// sessionStorage so a re-render or a screen change doesn't need another login.
// Falling back to a local table keeps the pre-login screens sane.
export const PLATFORM_FALLBACK = {
  bale:     { name_fa: "بله",    name_en: "Bale",     channel_url: "https://ble.ir/zoha_ghese" },
  telegram: { name_fa: "تلگرام", name_en: "Telegram", channel_url: "https://t.me/zoha_ghese" },
  eitaa:    { name_fa: "ایتا",   name_en: "Eitaa",    channel_url: "https://eitaa.com/zoha_ghese" },
  soroush:  { name_fa: "سروش‌پلاس", name_en: "Soroush Plus", channel_url: "https://splus.ir/zoha_ghese" },
  // The Android app: no channel, no gate, Bazaar billing only (services/platforms/bazaar.py).
  bazaar:   { name_fa: "بازار", name_en: "Bazaar", channel: "", channel_url: "", channel_enforced: false,
              payment: "bazaar_iab", installment: null, web_payment: null, supports_audio_send: false },
};

export function setPlatformInfo(info) {
  try { sessionStorage.setItem("platform_info", JSON.stringify(info || {})); } catch (_) {}
}
export function platformInfo() {
  try {
    const raw = JSON.parse(sessionStorage.getItem("platform_info") || "null");
    if (raw && raw.key) return raw;
  } catch (_) {}
  // Pre-login (or if the server didn't send platform_info): fall back to THIS
  // host's row, never Bale's. Getting this wrong is what put a ble.ir link in
  // front of Telegram users.
  const k = getPlatform();
  return { key: k, channel: "@zoha_ghese", channel_enforced: true,
           supports_payment: true, supports_support_chat: true, ...(PLATFORM_FALLBACK[k] || PLATFORM_FALLBACK.bale) };
}

// Display name of the messenger the Mini App is running inside — so no copy is
// hard-wired to "Bale". Served by the backend registry; adding a platform there
// is enough, nothing here needs editing.
export function messengerName() {
  const p = platformInfo();
  const n = moduleLang() === "en" ? p.name_en : p.name_fa;
  return n || (moduleLang() === "fa" ? "پیام‌رسان" : "the messenger");
}

// The referral code the parent arrived with, from EITHER carrier:
//   • `?ref=CODE`   — a web URL (and how the Mini App has always read it)
//   • start_param   — `?startapp=ref_CODE` on the messenger's own deep link, which
//                     the host hands us as initDataUnsafe.start_param
//
// ⚠️ The second one is why invite links were broken: build_referral_link() emitted
// `ble.ir/<bot>?start=CODE`, which opens the BOT CHAT — the payload goes to the
// webhook, never to this page — so the code was silently dropped and the friend had
// to type it by hand. The link is `?startapp=ref_<CODE>` now and lands here.
//
// ⚠️ Cached in localStorage, NOT sessionStorage, and that is the whole point of the
// cache. Both carriers only exist on the ONE launch that brought them — a start_param
// is not repeated on the next open, and the `?ref=` query is gone as soon as the app
// is reopened from the messenger's own menu button. Between arriving and paying, the
// friend adds a child and answers ~70 questions; that routinely spans several
// sessions, and sessionStorage dies with each one, so the invite was still being lost
// at the last step. localStorage is per-device and survives until checkout.
//
// Leaving a stale code behind is safe: the server re-validates at purchase and
// refuses self-referral, an already-invited buyer and any non-first purchase. It is
// cleared on a completed purchase anyway (clearPendingReferral).
const REF_PREFIX = "ref_";
const REF_KEY = "zoha_pending_ref";
export function pendingReferral() {
  let code = "";
  try { code = new URLSearchParams(window.location.search).get("ref") || ""; } catch (_) {}
  if (!code) {
    try {
      const sp = webApp()?.initDataUnsafe?.start_param || "";
      if (sp.startsWith(REF_PREFIX)) code = sp.slice(REF_PREFIX.length);
    } catch (_) {}
  }
  try {
    if (code) localStorage.setItem(REF_KEY, code);
    else code = localStorage.getItem(REF_KEY) || "";
  } catch (_) {}
  return code;
}

// Spent, or at least attempted — drop it so a later plan for a second child isn't
// still quoting a friend's first-purchase code the server will only reject.
export function clearPendingReferral() {
  try { localStorage.removeItem(REF_KEY); } catch (_) {}
  try { sessionStorage.removeItem("pending_ref"); } catch (_) {}   // the old key
}

// Onboarding is shown once PER USER (not per device) — so a new account on a
// shared device/webview still sees it. Falls back to a device-wide key if the
// WebApp user id isn't available.
export function onboardKey() {
  try {
    const uid = webApp()?.initDataUnsafe?.user?.id;
    return uid ? `zoha_onboarded_${uid}` : "zoha_seen_onboarding";
  } catch { return "zoha_seen_onboarding"; }
}

// The «ورود به ضحا قصه» welcome screen is a FIRST-VISIT screen. Once a user has
// passed it (or already has children / a plan) it must never come back — a
// returning parent should land straight on their home screen.
export function enteredKey() {
  return onboardKey().replace("onboarded", "entered").replace("zoha_seen_onboarding", "zoha_entered");
}

// Some messengers expose NO membership API (Eitaa), so the server can never say
// whether the parent joined the channel. There the gate is TRUST-BASED: show it
// once, remember the «عضو شدم ✓» tap per user, and never nag again. Verified
// platforms (Bale/Telegram) ignore this and keep using the server's verdict.
export function joinedKey() {
  return onboardKey().replace("onboarded", "joined").replace("zoha_seen_onboarding", "zoha_joined");
}
export function hasDismissedJoinGate() {
  try { return localStorage.getItem(joinedKey()) === "1"; } catch { return false; }
}
export function markJoinGateDismissed() {
  try { localStorage.setItem(joinedKey(), "1"); } catch (_) {}
}

